import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { therapistClient } from '../../../../PatientApps/apiClient';
import { getDisplayTime, ensureDateObject } from './timeUtils';

// Mock data generation for development environment
const mockPatients = [
  { name: 'John Doe', uuid: 'mock-uuid-1' },
  { name: 'Jane Smith', uuid: 'mock-uuid-2' },
  { name: 'Alice Johnson', uuid: 'mock-uuid-3' },
  { name: 'Alice Johnson2', uuid: 'mock-uuid-4' }
];

// Async thunks for API calls
// Fix for the fetchEvents thunk to properly handle production mode
// Updated fetchEvents thunk for calendar-slice.js

export const fetchEvents = createAsyncThunk(
  'calendar/fetchEvents',
  async (_, { rejectWithValue, getState }) => {
    const { i18n } = getState();
    const locale = i18n?.language || 'en';
    let events = []; // Declare events array here

    try {
      console.log('FETCH EVENTS - Starting fetchEvents thunk');
      console.log('FETCH EVENTS - NODE_ENV:', process.env.NODE_ENV);

      // Use mock data in development mode
      if (process.env.NODE_ENV === 'development') {
        console.log('FETCH EVENTS - Generating mock events (development mode)');
        // Generate mock events with standardized timezone info
        const startDate = new Date();
        startDate.setHours(0, 0, 0, 0);

        for (let i = 0; i < 10; i++) {
          const eventDate = new Date(startDate);
          eventDate.setDate(eventDate.getDate() + (i % 7));

          const patient = mockPatients[i % mockPatients.length];

          // Create the UTC timestamp - base time at 9, 10, 11 AM, etc.
          const utcTime = new Date(Date.UTC(
            eventDate.getFullYear(),
            eventDate.getMonth(),
            eventDate.getDate(),
            9 + (i % 8), // Hours in UTC
            0, 0, 0
          ));

          events.push({
            id: `mock-event-${i}`,
            title: `Session with ${patient.name}`,
            booked_session_date_timestamp: utcTime.toISOString(),
            start: utcTime.toISOString(),
            end: new Date(utcTime.getTime() + 60 * 60 * 1000).toISOString(),
            timezone: 'UTC',
            patient_uuid: patient.uuid,
            full_name: patient.name,
            session_uuid: `mock-session-${i}`,
            meeting_token: `mock-token-${i}`,
            description: `Session with ${patient.name}`,
            color: '#1976d2'
          });
        }

        console.log('FETCH EVENTS - Generated', events.length, 'mock events');
      }
      // Production mode - use real API
      else {
        console.log('FETCH EVENTS - Making API call to get real events');
        try {
          const response = await therapistClient.get('/api/protected/therapist/schedule');
          console.log('FETCH EVENTS - API response received:', response.status);

          if (!response.data) {
            console.error('FETCH EVENTS - No data in API response');
            return [];
          }

          // Transform API events
          events = response.data.map(event => {
            try {
              // Make sure we have a valid timestamp
              if (!event.booked_session_date_timestamp) {
                console.warn('FETCH EVENTS - Event missing timestamp:', event.session_uuid);
                return null;
              }
              
              // Parse the date directly from the timestamp
              const startDate = new Date(event.booked_session_date_timestamp);
              
              // If the date is invalid, log and return null
              if (isNaN(startDate.getTime())) {
                console.warn('FETCH EVENTS - Invalid timestamp:', event.booked_session_date_timestamp);
                return null;
              }
              
              // Calculate end time (1 hour after start)
              const endDate = new Date(startDate.getTime() + 60 * 60 * 1000);

              return {
                id: event.session_uuid,
                title: `Session with ${event.full_name || 'Unknown'}`,
                start: startDate.toISOString(),
                end: endDate.toISOString(),
                timezone: event.timezone || 'UTC', // Use event timezone if available
                patient_uuid: event.patient_uuid,
                full_name: event.full_name,
                session_uuid: event.session_uuid,
                meeting_token: event.meeting_token,
                description: `Session with ${event.full_name || 'Unknown'}`,
                color: '#1976d2',
                booked_session_date_timestamp: event.booked_session_date_timestamp
              };
            } catch (err) {
              console.error('FETCH EVENTS - Error processing event:', err, event);
              return null;
            }
          }).filter(Boolean); // Remove null events

          console.log('FETCH EVENTS - Transformed', events.length, 'events');
        } catch (apiError) {
          console.error('FETCH EVENTS - API call failed:', apiError);

          if (apiError.response && apiError.response.status === 401) {
            console.warn('FETCH EVENTS - Authentication error, returning empty array');
            return [];
          }

          return rejectWithValue(apiError.response?.data || apiError.message);
        }
      }
      return events; // Return the events array
    } catch (error) {
      console.error('FETCH EVENTS - Error in thunk:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchPatients = createAsyncThunk(
  'calendar/fetchPatients',
  async (_, { rejectWithValue }) => {
    try {
      if (process.env.NODE_ENV === 'development') {
        return mockPatients.map(patient => ({
          uuid: patient.uuid,
          full_name: patient.name,
          name: patient.name
        }));
      } else {
        const response = await therapistClient.get('/api/protected/therapist/clients');
        
        return response.data.map(patient => ({
          uuid: patient.patient_uuid,
          full_name: patient.patient_full_name,
          name: patient.patient_full_name
        }));
      }
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Updated createEvent and updateEvent thunks for calendar-slice.js

export const createEvent = createAsyncThunk(
  'calendar/createEvent',
  async ({ eventData, timezone }, { rejectWithValue, getState }) => {
    try {
      const { i18n } = getState();
      const locale = i18n?.language || 'en';
      
      // Ensure we have a proper Date object
      const localTime = ensureDateObject(eventData.start);
      
      if (process.env.NODE_ENV === 'development') {
        console.log('Creating event (development mode):', eventData);
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // For mock data, store the event with all timezone information
        return {
          id: Math.random().toString(36).substring(2, 9),
          title: eventData.title,
          // Store original date as ISO string
          start: localTime.toISOString(),
          end: new Date(localTime.getTime() + 60 * 60 * 1000).toISOString(),
          timezone: timezone, // Store the user's selected timezone
          patient_uuid: eventData.patient_uuid,
          full_name: eventData.patient_name || eventData.full_name || 'Patient',
          session_uuid: Math.random().toString(36).substring(2, 9),
          meeting_token: Math.random().toString(36).substring(2, 9),
          description: eventData.description || '',
          color: eventData.color || '#1976d2',
          booked_session_date_timestamp: localTime.toISOString()
        };
      } else {
        // For production, send ALL the data from eventData
        const apiData = {
          // Basic session info
          timezone: timezone,
          startTime: localTime.toISOString(),
          language: locale,
          
          // Patient info (existing user)
          ...(eventData.patient_uuid && { patient_uuid: eventData.patient_uuid }),
          
          // New user info (if provided)
          ...(eventData.email && { 
            email: eventData.email,
            firstName: eventData.firstName,
            lastName: eventData.lastName
          }),
          
          // Form data
          ...(eventData.forms && { forms: eventData.forms }),
          ...(eventData.questionnaires && { questionnaires: eventData.questionnaires }),
          
          // Other event data that might be needed
          ...(eventData.description && { description: eventData.description }),
          ...(eventData.color && { color: eventData.color })
        };
        
        console.log("Sending API data:", apiData);
        
        const response = await therapistClient.post('/api/protected/therapist/schedule', apiData);
        
        // The backend should return a session UUID in the response
        if (!response.data || !response.data.session_uuid) {
          console.error("Invalid response format:", response.data);
          return rejectWithValue("Invalid response from server");
        }
        
        console.log("API response:", response.data);
        
        // Since the backend doesn't return the timestamp we need, we'll use the original time
        return {
          id: response.data.session_uuid,
          session_uuid: response.data.session_uuid,
          title: eventData.title,
          // Use the original time - we'll convert for display in the UI
          start: localTime.toISOString(),
          end: new Date(localTime.getTime() + 60 * 60 * 1000).toISOString(),
          timezone: timezone,
          patient_uuid: response.data.patient_uuid || eventData.patient_uuid,
          full_name: eventData.full_name || eventData.patient_name || 'Unknown',
          description: eventData.description || '',
          color: eventData.color || '#1976d2',
          join_link: response.data.join_link,
          form_links: response.data.form_links || [],
          // Keep a reference to the original timestamp
          booked_session_date_timestamp: localTime.toISOString()
        };
      }
    } catch (error) {
      console.error("Event creation error:", error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateEvent = createAsyncThunk(
  'calendar/updateEvent',
  async ({ eventData, timezone }, { rejectWithValue, getState }) => {
    try {
      const { i18n } = getState();
      const locale = i18n?.language || 'en';
      
      // Store the selected time as is
      const localTime = ensureDateObject(eventData.start);
      
      if (process.env.NODE_ENV === 'development') {
        console.log('Updating event (development mode):', eventData);
        await new Promise(resolve => setTimeout(resolve, 500));
        
        return {
          ...eventData,
          // Store dates as ISO strings for consistency
          start: localTime.toISOString(),
          end: new Date(localTime.getTime() + 60 * 60 * 1000).toISOString(),
          timezone: timezone,
          booked_session_date_timestamp: localTime.toISOString()
        };
      } else {
        const apiData = {
          // Basic session info
          timezone: timezone,
          startTime: localTime.toISOString(),
          language: locale,
          session_uuid: eventData.session_uuid || eventData.id,
          
          // Patient info (existing user)
          ...(eventData.patient_uuid && { patient_uuid: eventData.patient_uuid }),
          
          // New user info (if provided)
          ...(eventData.email && { 
            email: eventData.email,
            firstName: eventData.firstName,
            lastName: eventData.lastName
          }),
          
          // Form data
          ...(eventData.forms && { forms: eventData.forms }),
          ...(eventData.questionnaires && { questionnaires: eventData.questionnaires }),
          
          // Other event data
          ...(eventData.description && { description: eventData.description }),
          ...(eventData.color && { color: eventData.color })
        };
        
        console.log("Sending update API data:", apiData);
        
        await therapistClient.post('/api/protected/therapist/schedule/edit', apiData);
        
        return {
          ...eventData,
          // Store dates as ISO strings for consistency
          start: localTime.toISOString(),
          end: new Date(localTime.getTime() + 60 * 60 * 1000).toISOString(),
          timezone: timezone,
          booked_session_date_timestamp: localTime.toISOString()
        };
      }
    } catch (error) {
      console.error("Event update error:", error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteEvent = createAsyncThunk(
  'calendar/deleteEvent',
  async (eventId, { rejectWithValue }) => {
    try {
      if (process.env.NODE_ENV === 'development') {
        console.log('Deleting event (development mode):', eventId);
        await new Promise(resolve => setTimeout(resolve, 500));
        return eventId;
      } else {
        await therapistClient.post('/api/protected/therapist/schedule/delete', {
          session_uuid: eventId
        });
        return eventId;
      }
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Timezone offsets for use in the Redux slice
const TIMEZONE_OFFSETS = {
  'UTC': 0,
  'America/New_York': -4, // EDT (-5 in winter)
  'America/Chicago': -5,  // CDT (-6 in winter)
  'America/Denver': -6,   // MDT (-7 in winter)
  'America/Los_Angeles': -7, // PDT (-8 in winter)
  'Europe/London': 1,     // BST (0 in winter)
  'Europe/Berlin': 2,     // CEST (1 in winter)
  'Asia/Tokyo': 9,
  'Australia/Sydney': 10  // AEST (11 in summer)
};

// Available timezone options - Now moved to timeUtils.js
export const timezoneOptions = [
  { value: 'America/New_York', label: 'US Eastern' },
  { value: 'America/Chicago', label: 'US Central' },
  { value: 'America/Denver', label: 'US Mountain' },
  { value: 'America/Los_Angeles', label: 'US Pacific' },
  { value: 'Asia/Tokyo', label: 'Japan' },
  { value: 'Australia/Sydney', label: 'Australia' },
  { value: 'Europe/London', label: 'UK' },
];

const calendarSlice = createSlice({
  name: 'calendar',
  initialState: {
    events: [],
    patients: [],
    selectedTimezone: 'America/New_York',
    currentDate: new Date().toISOString(), // Store as ISO string for serializability
    view: 'month',
    loading: false,
    error: null,
    notification: {
      open: false,
      message: '',
      severity: 'info'
    }
  },
  reducers: {
    setTimezone: (state, action) => {
      // Just update the timezone - view components will handle the conversion
      state.selectedTimezone = action.payload;
    },
    setCurrentDate: (state, action) => {
      // Always ensure we store strings, not Date objects
      if (action.payload instanceof Date) {
        state.currentDate = action.payload.toISOString();
      } else if (typeof action.payload === 'string') {
        state.currentDate = action.payload;
      } else {
        // Fallback
        state.currentDate = new Date().toISOString();
      }
    },
    setView: (state, action) => {
      state.view = action.payload;
    },
    setNotification: (state, action) => {
      state.notification = {
        open: true,
        ...action.payload
      };
    },
    clearNotification: (state) => {
      state.notification = {
        open: false,
        message: '',
        severity: 'info'
      };
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch events handlers
      .addCase(fetchEvents.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchEvents.fulfilled, (state, action) => {
        state.events = action.payload;
        state.loading = false;
      })
      .addCase(fetchEvents.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch events';
        state.notification = {
          open: true,
          message: `Error loading events: ${action.payload || 'Unknown error'}`,
          severity: 'error'
        };
      })
      // Fetch patients handlers
      .addCase(fetchPatients.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPatients.fulfilled, (state, action) => {
        state.patients = action.payload;
        state.loading = false;
      })
      .addCase(fetchPatients.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch patients';
        state.notification = {
          open: true,
          message: `Error loading patients: ${action.payload || 'Unknown error'}`,
          severity: 'error'
        };
      })
      // Create event handlers
      .addCase(createEvent.fulfilled, (state, action) => {
        state.events.push(action.payload);
        state.notification = {
          open: true,
          message: 'Event created successfully',
          severity: 'success'
        };
      })
      .addCase(createEvent.rejected, (state, action) => {
        state.error = action.payload;
        state.notification = {
          open: true,
          message: `Failed to create event: ${action.payload || 'Unknown error'}`,
          severity: 'error'
        };
      })
      // Update event handlers
      .addCase(updateEvent.fulfilled, (state, action) => {
        const index = state.events.findIndex(event => 
          event.id === action.payload.id || event.session_uuid === action.payload.session_uuid
        );
        if (index !== -1) {
          state.events[index] = action.payload;
        }
        state.notification = {
          open: true,
          message: 'Event updated successfully',
          severity: 'success'
        };
      })
      .addCase(updateEvent.rejected, (state, action) => {
        state.error = action.payload;
        state.notification = {
          open: true,
          message: `Failed to update event: ${action.payload || 'Unknown error'}`,
          severity: 'error'
        };
      })
      // Delete event handlers
      .addCase(deleteEvent.fulfilled, (state, action) => {
        state.events = state.events.filter(event => 
          event.id !== action.payload && event.session_uuid !== action.payload
        );
        state.notification = {
          open: true,
          message: 'Event deleted successfully',
          severity: 'info'
        };
      })
      .addCase(deleteEvent.rejected, (state, action) => {
        state.error = action.payload;
        state.notification = {
          open: true,
          message: `Failed to delete event: ${action.payload || 'Unknown error'}`,
          severity: 'error'
        };
      });
  }
});

export const { setTimezone, setCurrentDate, setView, setNotification, clearNotification } = calendarSlice.actions;
export default calendarSlice.reducer;